// INTEL CONFIDENTIAL
// Copyright (C) 2023 Intel Corporation

import { type OfferingsProps } from './Offerings'

interface OfferingsResource {
  title: string
  bodyText?: string
  footerText?: string
}

export interface PageResource {
  title: string
  text?: string
  offerings: OfferingsResource[]
}

export interface SiteProductsOfferings {
  compute: OfferingsProps
  kubernetes: OfferingsProps
  storage: OfferingsProps
  storagePricing: OfferingsProps
  openSoftwareArchitecture: OfferingsProps
  support: OfferingsProps
  enterpriseDeployments: OfferingsProps
  enterpriseBenefits: OfferingsProps
  enterpriseTransition: OfferingsProps
  developersResources: OfferingsProps
  developersLearning: OfferingsProps
  developersEcosystem: OfferingsProps
  developersUseCase: OfferingsProps
  startupsBuild: OfferingsProps
  startupsFounders: OfferingsProps
  startupsApplyNow: OfferingsProps
}

const documentationURL: any = process.env.DOCUMENTATION_URL
const tutorialsURL: any = process.env.TUTORIALS_URL
const notebooksURL: any = process.env.NOTEBOOKS_URL

export const productOfferings: SiteProductsOfferings = {
  compute: {
    title: 'On-demand single host bare metal and VM instances',
    offerings: [
      {
        title: 'AI Processor Instances',
        bodyText: 'Leverage specialized hardware for maximum performance in AI-intensive applications.',
        footerText: 'Starting at $1.30 / hr / card'
      },
      {
        title: 'GPU Instances',
        bodyText: 'Accelerate training and inference for demanding AI tasks.',
        footerText: 'Starting at $0.39 / hr / card'
      }
    ]
  },
  kubernetes: {
    title: 'Clusters - for heavy workloads',
    text: 'Physically interconnected clusters for high performance workloads. Enjoy the most price effective AI clusters for training, fine tuning and inference.',
    offerings: [
      {
        title: 'Kubernetes Clusters',
        bodyText:
          'Manage and scale your containerized applications with ease. Combine VM nodes with Bare metal GPUs and AI processors.',
        footerText: 'Starting at $1.30 / hr / card'
      },
      {
        title: 'Supercomputing',
        bodyText:
          'Needing power? Tailor your cluster configuration to your specific needs, deploy 100s of bare metal AI processor nodes.',
        footerText: 'Starting at $1.30 / hr / card'
      }
    ],
    salesText: 'For reserved instances (1 year or more):'
  },
  storage: {
    title: 'Storage service',
    text: 'Start storing data close to your compute workloads.',
    offerings: [
      {
        title: 'Filesystem',
        bodyText: 'Efficiently manage and store data with filesystem storage. ',
        footerText: 'Starting at $0.07 / GB / month'
      },
      {
        title: 'Object storage',
        bodyText: 'Perfect for storing large amounts of unstructured data.',
        footerText: 'Starting at $0.05 / GB / month'
      }
    ]
  },
  storagePricing: {
    title: 'Storage service',
    offerings: [
      {
        title: 'Filesystem',
        bodyText: 'Efficiently manage and store data with filesystem storage. ',
        footerText: 'Starting at $0.07 / GB / month'
      },
      {
        title: 'Object storage',
        bodyText: 'Perfect for storing large amounts of unstructured data.',
        footerText: 'Starting at $0.05 / GB / month'
      }
    ]
  },
  openSoftwareArchitecture: {
    title: 'Open software architecture',
    offerings: [
      {
        title: 'Learning catalog',
        bodyText:
          'Enjoy tenths of easy-to-follow jupyter notebooks. Learn about AI, quantum computing and reders for free on our shared environment.'
      },
      {
        title: 'Intel AI tools available',
        bodyText:
          'Our compute options are ready to use with utilites and drivers. Use OneAPI and AI kits on GPUs. Enjoy Pytorch through our compute.'
      },
      {
        title: 'Software catalog',
        bodyText:
          'A curated collection of software products and utilities. Find solutions that accelerate your development process.'
      }
    ],
    customClass: 'lightBlue'
  },
  support: {
    title: 'Available channels to enterprise accounts',
    offerings: [
      {
        title: 'Chat',
        bodyText: 'Get written response to our support in a 24x7'
      },
      {
        title: 'Phone support',
        bodyText: 'Get live help 24x7'
      }
    ]
  },
  enterpriseDeployments: {
    title: 'AI cloud supports your AI journey, from experimentation to large AI deployments',
    text: 'Our cloud platform is designed to support both experimentation and production deployments. Easily test and iterate on new AI models and applications, then seamlessly scale up for production workloads.',
    offerings: [
      {
        title: 'Compute instances',
        bodyText: 'On-demand GPUs, AI processors and CPUs.'
      },
      {
        title: 'Clusters',
        bodyText: 'Manage and scale your containerized AI applications with ease.'
      }
    ]
  },
  enterpriseBenefits: {
    title: 'Why Tiber AI cloud is the best for your needs?',
    offerings: [
      {
        title: 'Superior performance',
        bodyText: 'Our high-performance machines can train models up to 30% faster than competing platforms.'
      },
      {
        title: 'Scalability',
        bodyText: 'Easily scale your infrastructure up or down to meet your growing AI needs.'
      },
      {
        title: 'Flexibility',
        bodyText: 'Choose from a variety of instance types, storage options, and software tools.'
      },
      {
        title: 'Security',
        bodyText: 'Benefit from our robust security measures to protect your data and applications.'
      },
      {
        title: 'Support',
        bodyText: 'Our dedicated support team is available to assist you with any questions or issues.'
      },
      {
        title: 'Developer program',
        bodyText: 'Lots of resources for your developers:  documentation, tutorials and code samples.'
      }
    ],
    customClass: 'lightBlue'
  },
  enterpriseTransition: {
    title: 'Seamless transition to AI Cloud',
    text: 'Switching to our cloud platform is easy. Our experienced team can help you migrate your existing AI workloads with minimal disruption. Benefit from our flexible infrastructure and customizable options to tailor your environment to your specific needs.',
    offerings: [
      {
        title: 'Open source stack',
        bodyText:
          'Train on Intel® CPUs and GPUs and integrate  your AI development workflow with Intel®-optimized, deep learning frameworks, pretrained models, and model optimization tools. '
      },
      {
        title: 'Cost effectiveness',
        bodyText:
          'Perform your tasks in less time with Intel Gaudi Bare Metal instances. Enjoy committed use discounts in our enterprise tier.'
      }
    ],
    showSalesButton: true
  },
  developersResources: {
    title: 'Resources for developers',
    offerings: [
      {
        title: 'Documentation',
        bodyText: 'User guides, FAQs, troubleshooting, and tutorials.',
        actions: [
          {
            label: 'Go to documentation',
            type: 'link',
            redirectTo: documentationURL,
            openInNewTab: false
          }
        ]
      },
      {
        title: 'Tutorials',
        bodyText: 'Perform complex AI tasks as fast as possible.',
        actions: [
          {
            label: 'View tutorials',
            type: 'link',
            redirectTo: tutorialsURL,
            openInNewTab: false
          }
        ]
      },
      {
        title: 'Hands-on labs',
        bodyText: 'Learn AI/ML development for free in ready-made Jupyter Notebooks.',
        actions: [
          {
            label: 'Browse notebooks',
            type: 'link',
            redirectTo: notebooksURL,
            openInNewTab: false
          }
        ]
      }
    ],
    customClass: 'lightBlue'
  },
  developersLearning: {
    title: 'Start now for free with our hands-on learning catalog',
    offerings: [
      {
        title: 'Access to shared infrastructure',
        bodyText: 'Get access to shared GPUs and AI processors for free for your experiments.'
      },
      {
        title: 'Powered by JupyterLab',
        bodyText: 'Use jupyter notebooks guided learning to execute code easily.'
      },
      {
        title: 'ML, AI and other topics',
        bodyText: 'Choose from a variety of topics and technologies, from ML, LLMs to render kit and quantum computing.'
      }
    ],
    showSignupButton: true
  },
  developersEcosystem: {
    title: 'Learn about our open ecosystem',
    offerings: [
      {
        title: 'OneAPI',
        bodyText: 'Highly productive development stack for AI and open, accelerated computing.'
      },
      {
        title: 'Pytorch',
        bodyText: 'Get the best PyTorch training and inference performance on Intel CPU or GPU hardware.'
      },
      {
        title: 'OPEA',
        bodyText:
          'Get GenAI pipelines on secure solutions based on Intel® Xeon® processors and Intel® Gaudi® AI accelerators.'
      }
    ],
    customClass: 'lightBlue'
  },
  developersUseCase: {
    title: 'Explore resources by use case',
    offerings: [
      {
        title: 'Bots'
      },
      {
        title: 'Inference'
      },
      {
        title: 'Model training'
      },
      {
        title: 'Fine-tuning'
      },
      {
        title: 'Performance benchmarking'
      },
      {
        title: 'App deployments'
      }
    ]
  },
  startupsBuild: {
    title: 'Build the Next Big Thing with Intel® Tiber™ AI Cloud',
    text: 'Intel® Tiber™ AI Cloud provides startups with the computing power, flexibility, and tools they need to scale their AI solutions.',
    offerings: [
      {
        title: 'Scalable AI Infrastructure',
        bodyText: [
          'High-performance compute power with Intel® Gaudi2 AI accelerators, Intel® Xeon CPUs and Intel® GPUs.',
          'Support for large-scale model training and inference.',
          'Benefit from Intel®’s software stack and credits for evaluations, making it easier to integrate with Intel®’s ecosystem without upfront costs.'
        ]
      },
      {
        title: 'Access to Exclusive Intel AI Software, Hardware & Resources',
        bodyText: [
          'Free cloud credits, Intel® OpenVINOTM , Intel® oneAPI, and AI toolkits.',
          'Early access to Intel’s latest AI hardware.',
          'Including access to preview hardware before it hits the production market.'
        ]
      },
      {
        title: 'Expert Guidance & Startup Support',
        bodyText: [
          'One-on-one mentoring from Intel engineers to refine AI models.',
          'Go-to-market strategy support to accelerate adoption and scale faster.',
          'Exclusive networking with industry leaders, investors, and AI innovators.'
        ]
      }
    ],
    customClass: 'lightBlue'
  },
  startupsFounders: {
    title: 'What founders say',
    offerings: [
      {
        bodyText:
          'Testing on latest Intel products provides us with necessary insights, to advise our customers, running AI workloads on their on-prem infrastructure.  Intel® Tiber™ AI Cloud is the perfect testing ground to get tangible performance data.',
        footerText: 'Markus Zarbock, CEO Starwit Technologies GmbH'
      },
      {
        bodyText:
          'We were very impressed with how easy it is to use Intel Gaudi VMs with our Pytorch models. Profiling, optimizing, inference - everything worked out of the box.',
        footerText: 'Rachit Singh, CTO, Amplify Renewables'
      },
      {
        bodyText:
          'With the team support at Intel® Liftoff, we felt comfortable pushing our boundaries and trying something risky.  Without this program, we would have not explored LLMs in our application.',
        footerText: 'Markus Zarbock, CEO Starwit Technologies GmbH'
      }
    ],
    customClass: 'lightBlue',
    showApplyNowButton: true,
    redirectTo: process.env.STARTUP_APPLY_URL
  },
  startupsApplyNow: {
    title: 'Apply Now: Launch Your AI Startup',
    text: 'The only AI startup accelerator application you’ll ever need to make: access cutting-edge compute, expert support, and a global AI network, no equity required.',
    offerings: [
      {
        title: 'Who can apply?',
        bodyText: [
          'Early-stage AI startups developing innovative solutions',
          'Teams looking to optimize models with Intel hardware and software',
          'Founders ready to scale with expert guidance and industry connections'
        ]
      },
      {
        title: 'Application Process',
        bodyText: [
          'Apply: Submit your startup details.',
          'Get Accepted: Access Intel’s AI ecosystem.',
          'Build & Scale: Leverage compute, software, and world-leading expertise.'
        ]
      }
    ],
    customClass: 'lightBlue',
    showApplyNowButton: true,
    redirectTo: process.env.STARTUP_APPLY_URL
  }
}
